import * as React from 'react';
import Modal from 'antd/lib/modal';
import Radio from 'antd/lib/radio';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import { ReleaseWithCustomAppStoreSettings } from '@/types/stepConfig';

export enum UpdateReleaseSettingsScope {
    ExcludeCustomisedReleases = 'excludeCustomisedReleases',
    IncludeCustomisedReleases = 'includeCustomisedReleases',
}

interface UpdateReleaseSettingsScopeModalProps {
    open: boolean;
    releases: ReleaseWithCustomAppStoreSettings[];
    isLoading: boolean;
    onConfirm: (scope: UpdateReleaseSettingsScope) => void;
    onCancel: () => void;
}

const formatVersions = (releases: ReleaseWithCustomAppStoreSettings[]): string => {
    const versions = releases.map((release) => release.version);
    if (versions.length <= 1) {
        return versions.join('');
    }
    return `${versions.slice(0, -1).join(', ')} and ${versions[versions.length - 1]}`;
};

const UpdateReleaseSettingsScopeModal: React.FC<UpdateReleaseSettingsScopeModalProps> = ({
    open,
    releases,
    isLoading,
    onConfirm,
    onCancel,
}) => {
    const [scope, setScope] = React.useState(UpdateReleaseSettingsScope.ExcludeCustomisedReleases);

    React.useEffect(() => {
        if (open) {
            setScope(UpdateReleaseSettingsScope.ExcludeCustomisedReleases);
        }
    }, [open]);

    const versions = formatVersions(releases);
    const releaseNoun = releases.length === 1 ? 'release' : 'releases';
    const willOverride = scope === UpdateReleaseSettingsScope.IncludeCustomisedReleases;

    return (
        <Modal
            open={open}
            title="Override app store settings that were edited previously?"
            okText={willOverride ? 'Update and override' : 'Update and do not override'}
            cancelText="Cancel"
            okButtonProps={{ loading: isLoading }}
            onOk={() => onConfirm(scope)}
            onCancel={onCancel}
            destroyOnClose
        >
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Text>
                    The app store settings for {releaseNoun} {versions} were already edited directly from the{' '}
                    {releaseNoun}. Choose whether this new change should override them:
                </Text>
                <Radio.Group value={scope} onChange={(event) => setScope(event.target.value)}>
                    <Space direction="vertical">
                        <Radio value={UpdateReleaseSettingsScope.ExcludeCustomisedReleases}>
                            Update all active releases excluding {versions}
                        </Radio>
                        <Radio value={UpdateReleaseSettingsScope.IncludeCustomisedReleases}>
                            Update all active releases including {versions}
                        </Radio>
                    </Space>
                </Radio.Group>
            </Space>
        </Modal>
    );
};

export default UpdateReleaseSettingsScopeModal;
